import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Entry } from 'contentful';
import { consentFeatures } from 'src/app/model/page/accept-consent-page';
import { FileUploads, Response, VideoDetails } from 'src/app/model/page/file-upload';
import { ApiService } from 'src/app/service/api.service';
import { entraAuthService } from 'src/app/service/auth.service';
import { ContentfulService } from 'src/app/service/contentful.service';
import { ConfigService } from 'src/app/service/config.service';
import { ModelValidator } from 'src/app/validator/model-validator';

import { documentToHtmlString } from '../../../model/shared/index-consetPpoup';
import { config } from 'src/environments/environment';

@Component({
  selector: 'app-upload',
  templateUrl: 'file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnInit {
  consentFeatures: consentFeatures = {} as consentFeatures;
  selectedFile: File | undefined;
  response: Response = {} as Response;
  isValidFile: boolean = false;
  isClicked: boolean = false;
  fileName: string = '';
  fileList: string[] = [];
  selectedVideoName: string = '';
  videoPlayUrl: string = '';
  deleteVideoUrl: string = '';
  videoListDetails: FileUploads = {} as FileUploads;

  @ViewChild('videoElement')
  videoRef!: ElementRef;
  @ViewChild('fileInput')
  fileInput!: ElementRef;
  @ViewChild('consentAccept')
  isConsentAccepted!: ElementRef;

  constructor(private apiService: ApiService, private contentfulService: ContentfulService, private entraAuthService:entraAuthService, private configService: ConfigService) {}

  ngOnInit(): void {
    this.isClicked = true;
    this.getVideoList();
    this.consentFeatures.consentSectionCard = [];
    this.consentFeatures.sections = [];

    const query = { 'fields.title': 'Accept-Consent-Popup-data' };
    this.contentfulService.getPageContentResponse(query).then((fileUploadPageEntries) => {
      this.mapCards(fileUploadPageEntries.includes.Entry);
    });
  }

  private consentPopupCard(entry: Entry<any>) {
    let headerImage: any = entry.fields['headImage'];
    this.consentFeatures.consentSectionCard.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      headImage: headerImage?.fields?.file?.url,
      buttonText: entry.fields['buttonText'] as string,
      buttonUrl: entry.fields['buttonUrl'] as string,
      richTextBody: entry.fields['body'] as string
    });

    this.consentFeatures.consentSectionCard.sort((a, b) => a.order - b.order);
  }

  private mapCards(consentPopupPageEntries: Entry<any>[]) {
    consentPopupPageEntries
      .filter((entry) => ModelValidator.isCard(entry))
      .forEach((entry) => {
        this.consentPopupCard(entry);
      });
  }

  async getVideoList() {
    if (this.isClicked) {
      this.fileList = [];
      const apiResponse = await this.apiService.sendGetRequest('/video-list');
      if (apiResponse.success) {
        this.videoListDetails = apiResponse.success;
        if (this.videoListDetails.videosList !== null) {
          this.videoListDetails.videosList.forEach((videoList: VideoDetails) => {
            this.fileList.push(videoList.videos.split('/')[1]);
          });
        }
      } else if (apiResponse.error) {
        this.response = { message: apiResponse.error.text, color: 'errorMessage' };
          localStorage.setItem('redirect_url', '/profile');
          this.redirectToLogin();       
      }
    }
    this.isClicked = false;
  }
  returnHtmlFromRichText(richText: any): string {
    if (richText === undefined || richText === null || richText.nodeType !== 'document') {
      return '<p>Error</p>';
    }

    return documentToHtmlString(richText);
  }

  validateFile(event: any) {
    this.response = { message: '', color: '' };
    this.selectedFile = event.target.files[0];
    const allowedTypes: string[] = ['video/mp4', 'video/quicktime'];
    const maxFileSizeInBytes = 100000000;
    const maxFileLength = 104;

    if (!this.selectedFile) {
      this.response = { message: 'Please choose a file to upload.', color: 'errorMessage' };
      this.isValidFile = false;
      return;
    }

    const { type, size, name } = this.selectedFile;
    if (!allowedTypes.includes(type)) {
      this.response = { message: 'Invalid file type. Please select a file in the provided format.', color: 'errorMessage' };
      this.isValidFile = false;
      return;
    }

    if (size < 0) {
      this.response = { message: 'The selected file is empty. Please choose a valid file.', color: 'errorMessage' };
      this.isValidFile = false;
      return;
    }

    if (size >= maxFileSizeInBytes) {
      this.response = { message: 'The selected file is over 100MB. Please reduce the file size and try again.', color: 'errorMessage' };
      this.isValidFile = false;
      return;
    }

    if (name.endsWith('.mp4') || name.endsWith('.mov')) {
      const updateName = name.replace(/\.mp4|\.mov/, '');
      if (!/^\w+$/.test(updateName)) {
        this.response = { message: 'The file is not matching with the naming convention', color: 'errorMessage' };
        this.isValidFile = false;
        return;
      }
    }

    if (name.length > maxFileLength) {
      this.response = { message: 'The file name is too long. Please rename it to be less than 100 characters.', color: 'errorMessage' };
      this.isValidFile = false;
      return;
    }
    this.isValidFile = true;
    this.fileName = name;
  }

  async playVideo(i: number) {
    this.videoPlayUrl = this.videoListDetails.videosList[i].playUrl;
  }

  deleteConfirmation(i: number) {
    this.videoPlayUrl = '';
    this.isClicked = true;
    this.fileInput.nativeElement.value = null;
    if (this.videoListDetails && this.videoListDetails.videosList[i]) {
      if (this.videoListDetails.videosList[i].videos !== null) {
        this.selectedVideoName = this.videoListDetails.videosList[i].videos;
      }
      if (this.videoListDetails.videosList[i].deleteUrl !== null) {
        this.deleteVideoUrl = this.videoListDetails.videosList[i].deleteUrl;
      }
    }
  }

  async deleteFile() {
    if (this.deleteVideoUrl) {
      const apiResponse = await this.apiService.sendDeleteRequestWithoutDomain(this.deleteVideoUrl);
      if (apiResponse.success === null) {
        this.response = { message: 'Your file ' + this.selectedVideoName.split('/')[1] + ' has been deleted successfully.', color: 'successMessage' };
        await this.getVideoList();
      } else if (apiResponse.error) {
        this.response = { message: apiResponse.error.text, color: 'errorMessage' };
        if (apiResponse.error.status == 401) {
          localStorage.setItem('redirect_url', '/profile');
          this.redirectToLogin()        
        }
      }
    }
    this.isValidFile = false;
  }

  openConsent() {
    let consent = this.isConsentAccepted.nativeElement;
    const acceptConsent = localStorage.getItem('acceptConsent');
    if (acceptConsent == 'true') {
      this.uploadFile();
    } else {
      if (consent.id === 'acceptConsent') {
        consent.style.display = 'block';
      }
    }
  }

  async uploadFile() {
    this.isClicked = true;
    this.videoPlayUrl = '';
    let fileName = this.fileName;
    if (this.isValidFile && fileName !== '') {
      let queryParams = new HttpParams();
      queryParams = queryParams.append('fileName', fileName);
      const apiResponse = await this.apiService.sendGetRequest('/upload-video', queryParams);
      this.fileInput.nativeElement.value = null;

      if (apiResponse.success) {
        let apiResponseJson = JSON.parse(apiResponse.success);
        let presignedUrl: string = apiResponseJson.response;
        if ((typeof presignedUrl !== 'undefined' || presignedUrl !== '') && this.selectedFile !== undefined) {
          let apiPutResponse = await this.apiService.sendPutRequest(presignedUrl, new Blob([this.selectedFile], { type: this.selectedFile.type }), this.selectedFile.type);
          if (apiPutResponse.error) {
            if (apiPutResponse.error.statusText === 'Unknown Error') {
              this.response = { message: 'The file you are trying to upload is missing. Please select it again.', color: 'errorMessage' };
              this.isClicked = false;
            }
          } else {
            this.response = { message: 'Your file ' + fileName + '  has been uploaded successfully.', color: 'successMessage' };
            await this.getVideoList();
          }
        }
      } else if (apiResponse.error) {
        if (apiResponse.error.status == 200) {
          this.response = { message: 'The video upload limit has been exceeded.', color: 'errorMessage' };
        }
        if (apiResponse.error.status == 502) {
          this.response = { message: 'Unable to upload the video. Please try after sometime.', color: 'errorMessage' };
        }
        if (apiResponse.error.status == 401) {
          localStorage.setItem('redirect_url', '/profile');
          this.redirectToLogin()        
        }
      }
    } else {
      this.response = { message: `Error in uploading ${fileName}`, color: 'errorMessage' };
    }
    this.isValidFile = false;
  }

  consent(value: string) {
    let consent: any = this.isConsentAccepted.nativeElement;
    if (consent.id === 'acceptConsent' && value === 'accept') {
      localStorage.setItem('acceptConsent', 'true');
      this.uploadFile();
    }
    consent.style.display = 'none';
  }

  closeVideoPopup(event: any) {
    const classList = event.target.className;
    if (classList.includes('show')) {
      this.videoRef.nativeElement.pause();
    }
  }

  redirectToLogin(){
    this.entraAuthService.initLogin();  
  }
}
